import React, { useState } from 'react';
import { Anchor, Target } from 'lucide-react';

const BattleshipBoard = ({ gameState, isMyTurn, onMove, currentUserId }) => {
  const [selectedCell, setSelectedCell] = useState(null);
  
  // Determine which player's data to use
  const isPlayer1 = gameState?.player1_id === currentUserId;
  const myPlayerData = isPlayer1 ? gameState?.player1 : gameState?.player2;
  const opponentPlayerData = isPlayer1 ? gameState?.player2 : gameState?.player1;
  
  const phase = gameState?.phase || 'setup';
  const winner = gameState?.winner;
  const isDraw = gameState?.isDraw;
  const isGameOver = winner || isDraw;
  
  // Convert ship/hit/miss data into visual board grids
  const convertToBoard = (playerData, hideShips = false) => {
    const board = Array(10).fill(null).map(() => Array(10).fill(0));
    
    if (!playerData) return board;
    
    // Place ships
    if (!hideShips && playerData.ships) {
      playerData.ships.forEach(ship => {
        ship.positions.forEach(pos => {
          board[pos.row][pos.col] = ship.name;
        });
      });
    }
    
    // Mark hits
    if (playerData.hits) {
      playerData.hits.forEach(hit => {
        board[hit.row][hit.col] = 'hit';
      });
    }
    
    // Mark misses
    if (playerData.misses) {
      playerData.misses.forEach(miss => {
        board[miss.row][miss.col] = 'miss';
      });
    }
    
    return board;
  };
  
  const myBoard = convertToBoard(myPlayerData, false);
  const opponentBoard = convertToBoard(opponentPlayerData, true);
  
  const shipNames = {
    5: 'Carrier',
    4: 'Battleship',
    3: 'Cruiser',
    3.1: 'Submarine',
    2: 'Destroyer'
  };
  
  const handleCellClick = (row, col, isMyBoard) => {
    if (isGameOver) return;
    
    if (phase === 'playing' && !isMyBoard && isMyTurn) {
      // Attack opponent's board
      const cell = opponentBoard[row][col];
      if (cell !== 'hit' && cell !== 'miss') {
        onMove({ row, col });
        setSelectedCell(null);
      }
    }
  };
  
  const renderCell = (cell, row, col, isMyBoard) => {
    const isHit = cell === 'hit';
    const isMiss = cell === 'miss';
    const hasShip = cell && cell !== 'hit' && cell !== 'miss' && cell !== 0;
    
    let bgColor = 'bg-blue-600';
    let content = null;
    
    if (isHit) {
      bgColor = 'bg-red-500';
      content = (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-sm sm:text-base md:text-xl"></div>
        </div>
      );
    } else if (isMiss) {
      bgColor = 'bg-blue-400';
      content = (
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 md:w-2 md:h-2 rounded-full bg-white/50" />
        </div>
      );
    } else if (hasShip && isMyBoard) {
      // Show ships only on player's own board
      bgColor = 'bg-slate-700';
      content = (
        <div className="w-full h-full flex items-center justify-center">
          <Anchor className="text-white/70" size={12} />
        </div>
      );
    }
    
    return (
      <button
        onClick={() => handleCellClick(row, col, isMyBoard)}
        disabled={isGameOver || !isMyTurn || isMyBoard || isMiss || isHit}
        className={`
          w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 border border-white/20 transition-all duration-200
          ${bgColor}
          ${!isMyBoard && phase === 'playing' && !isHit && !isMiss && isMyTurn && !isGameOver
            ? 'hover:bg-yellow-400/30 hover:border-yellow-400 cursor-pointer'
            : ''
          }
          ${selectedCell?.row === row && selectedCell?.col === col ? 'ring-2 ring-yellow-400' : ''}
        `}
      >
        {content}
      </button>
    );
  };
  
  const renderBoard = (board, isMyBoard, title) => {
    if (!board || board.length === 0) {
      return (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-white/70 text-center">{title}</h3>
          <div className="w-[400px] h-[400px] flex items-center justify-center bg-blue-900/20 border-2 border-white/10 rounded-lg">
            <p className="text-white/40">Waiting for boards...</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-white/70 text-center">{title}</h3>
        <div className="relative">
          {/* 10x10 Grid */}
          <div className="grid grid-cols-10 gap-0 border-2 border-white/20 rounded-lg overflow-hidden shadow-xl">
            {board.map((row, rowIndex) => (
              row.map((cell, colIndex) => (
                <div key={`${rowIndex}-${colIndex}`}>
                  {renderCell(cell, rowIndex, colIndex, isMyBoard)}
                </div>
              ))
            ))}
          </div>
          
          {/* Row labels */}
          <div className="absolute -left-5 sm:-left-6 top-0 h-full flex flex-col justify-around text-white/30 text-[10px] sm:text-xs font-mono">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
              <div key={num} className="h-5 sm:h-6 md:h-8 flex items-center">{num}</div>
            ))}
          </div>
          
          {/* Column labels */}
          <div className="absolute -bottom-5 sm:-bottom-6 left-0 w-full flex justify-around text-white/30 text-[10px] sm:text-xs font-mono">
            {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'].map(letter => (
              <div key={letter} className="w-5 sm:w-6 md:w-8 text-center">{letter}</div>
            ))}
          </div>
        </div>
      </div>
    );
  };
  
  const countShipsRemaining = (playerData) => {
    if (!playerData || !playerData.ships) return 0;
    // Count ships that aren't fully sunk
    return playerData.ships.filter(ship => ship.hits.length < ship.size).length;
  };
  
  const myShipsRemaining = countShipsRemaining(myPlayerData);
  const opponentShipsRemaining = countShipsRemaining(opponentPlayerData);
  
  return (
    <div className="p-2 sm:p-3 md:p-4 flex flex-col items-center gap-2 sm:gap-3 md:gap-4">
      {/* Status */}
      <div className="text-center">
        {phase === 'placement' ? (
          <div className="space-y-2">
            <p className="text-lg font-semibold text-yellow-400">⚓ Placement Phase</p>
            <p className="text-sm text-white/50">Waiting for players to place ships...</p>
          </div>
        ) : isGameOver ? (
          <div className="space-y-2">
            {winner ? (
              <>
                <p className="text-2xl font-bold text-emerald-400">
                  {winner === currentUserId ? ' Victory!' : ' Defeated'}
                </p>
                <p className="text-sm text-white/50">
                  {winner === currentUserId ? 'All enemy ships destroyed!' : 'Your fleet was destroyed'}
                </p>
              </>
            ) : isDraw ? (
              <p className="text-xl text-yellow-400">It's a Draw!</p>
            ) : null}
          </div>
        ) : (
          <div className="space-y-2">
            <div className={`text-lg font-semibold ${isMyTurn ? 'text-emerald-400' : 'text-white/50'}`}>
              {isMyTurn ? 'Your Turn - Fire!' : 'Opponent\'s Turn'}
            </div>
            {isMyTurn && (
              <p className="text-xs text-white/40">Click on opponent's board to attack</p>
            )}
            <div className="flex gap-6 justify-center text-sm mt-2">
              <div className="flex items-center gap-2">
                <Anchor className="text-emerald-400" size={16} />
                <span className="text-white/70">Your Ships: {myShipsRemaining}</span>
              </div>
              <div className="flex items-center gap-2">
                <Target className="text-red-400" size={16} />
                <span className="text-white/70">Enemy Ships: {opponentShipsRemaining}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Boards */}
      {phase === 'setup' ? (
        <div className="text-center text-white/50">
          <p className="text-lg">⏳ Setting up the battlefield...</p>
          <p className="text-xs mt-2">Ships are being placed</p>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 md:gap-8 items-start">
          {renderBoard(opponentBoard, false, " Enemy Waters")}
          {renderBoard(myBoard, true, "⚓ Your Fleet")}
        </div>
      )}
      
      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs justify-center">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-blue-600 border border-white/20" />
          <span className="text-white/70">Water</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-slate-700 border border-white/20 flex items-center justify-center">
            <Anchor className="text-white/70" size={12} />
          </div>
          <span className="text-white/70">Your Ship</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-red-500 border border-white/20 flex items-center justify-center text-sm">
            
          </div>
          <span className="text-white/70">Hit</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-blue-400 border border-white/20 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-white/50" />
          </div>
          <span className="text-white/70">Miss</span>
        </div>
      </div>
    </div>
  );
};

export default BattleshipBoard;
